import { LoadingButton } from '@mui/lab';
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Alert,
  AlertTitle,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Transition } from '../../../components/Transition';
import { ADVERTISE_STATUS } from '../../../constants';
import { useFormik } from 'formik';
import changeAdvertiseStatusSchema from '../../../validation/changeAdvertiseStatusSchema';
import { createChangeAdvertiseStatus, bulkChangeAdvertiseStatus } from '../../../store/changeAdvertiseStatus/api';
import { useAppDispatch, useAppSelector } from '../../../store';
import { Advertise } from '../../../store/advertise/api/interface';
import { decryptString } from '../../../utils/url';
import { useParams } from 'react-router-dom';
import { AdvertiseStatus, IParams } from '../../../enum';
import { setStatus } from '../../../store/advertise/advertiseSlice';
import { getAdvertise } from '../../../store/advertise/api';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Typography } from '@mui/material';
import { publishAdsWithCredits } from '../../../services/company-credits';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Define a type for the bulk action properties
type BulkActionAdvertise = {
  id?: string;
  status?: string;
  isBulkAction?: boolean;
  selectedIds?: string[];
};

interface IProps {
  toggle: boolean;
  onClose: () => void;
  advertise?: Advertise | BulkActionAdvertise;
  setAdvertise: React.Dispatch<React.SetStateAction<Advertise | undefined>>;
  actionType?: 'changeStatus' | 'extendExpiry'; // New prop to distinguish action type
}

// Type guard to check if the advertise object has bulk action properties
function hasBulkActionProps(advertise: any): advertise is BulkActionAdvertise {
  return advertise && ('isBulkAction' in advertise || 'selectedIds' in advertise);
}

interface IInitialState {
  advertiseId: string;
  status: string;
  rejectionReason?: string;
  endDate?: Date | null; // Add endDate for extend expiry
  // For bulk operations
  isBulkAction?: boolean;
  advertiseIds?: string[];
}

const ChangeAdvertiseStatusFormModal: React.FC<IProps> = ({
  toggle,
  onClose,
  setAdvertise,
  advertise,
  actionType = 'changeStatus', // Default to changeStatus
}) => {
  const dispatch = useAppDispatch();
  const params = useParams<IParams>();
  const advertiseId = decryptString(params?.id ?? '');
  const [documentError, setDocumentError] = useState<string | null>(null);
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  const isCompanyAdmin = userRole === 'company';
  
  // Determine if this is for extending expiry
  const isExtendExpiry = actionType === 'extendExpiry' || 
    (advertise?.status === AdvertiseStatus.published || advertise?.status === AdvertiseStatus.expired);

  const {
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
  } = useFormik<IInitialState>({
    initialValues: {
      advertiseId: advertiseId || '', // Ensure advertiseId is always a string
      status: '',
      rejectionReason: '',
      endDate: null, // Initialize endDate
      isBulkAction: hasBulkActionProps(advertise) ? advertise.isBulkAction : false,
      advertiseIds: hasBulkActionProps(advertise) ? advertise.selectedIds : [],
    },
    validationSchema: changeAdvertiseStatusSchema,
    onSubmit: async (values) => {
      try {
        console.log("Form submitted with values:", values);

        // If extending expiry, send endDate instead of status
        if (isExtendExpiry && values.endDate) {
          await dispatch(
            createChangeAdvertiseStatus({
              advertiseId: values.advertiseId,
              endDate: values.endDate, // Send endDate as Date object
            })
          ).unwrap();

          dispatch(setStatus({ status: 'idle' }));
          
          if (advertiseId) {
            const updatedRecord = await dispatch(
              getAdvertise({ id: advertiseId })
            ).unwrap();
            setAdvertise(updatedRecord);
          }
          
          toast.success('Expiry date extended successfully');
          onClose();
          resetForm();
          return;
        }

        if (values?.status !== AdvertiseStatus.rejected) {
          delete values?.rejectionReason;
        }

        // For company admins, when publishing, use credit system
        if (isCompanyAdmin && values.status === AdvertiseStatus.published) {
          // Handle bulk operations with credits
          if (values.isBulkAction && values.advertiseIds && values.advertiseIds.length > 0) {
            console.log("Publishing bulk ads with credits:", {
              advertiseIds: values.advertiseIds,
            });
            
            const result = await publishAdsWithCredits({
              advertiseIds: values.advertiseIds,
            });
            
            toast.success(result.message || `${result.publishedCount} ads published successfully using credits.`);
            dispatch(setStatus({ status: 'idle' }));
            onClose();
            resetForm();
            setAdvertise(undefined);
            return;
          } 
          // Handle single advertisement with credits
          else if (values.advertiseId) {
            console.log("Publishing single ad with credits:", {
              advertiseId: values.advertiseId,
            });
            
            const result = await publishAdsWithCredits({
              advertiseIds: [values.advertiseId],
            });
            
            toast.success(result.message || 'Ad published successfully using credits.');
            dispatch(setStatus({ status: 'idle' }));
            
            // Only fetch record if we're in detail view
            if (advertiseId) {
              const updatedRecord = await dispatch(
                getAdvertise({ id: advertiseId })
              ).unwrap();
              setAdvertise(updatedRecord);
            }
            
            onClose();
            resetForm();
            return;
          }
        }
        
        // For non-publish status or non-company users, use regular status change
        // Handle bulk operations
        if (values.isBulkAction && values.advertiseIds && values.advertiseIds.length > 0) {
          console.log("Sending bulk request with:", {
            advertiseIds: values.advertiseIds,
            status: values.status,
            ...(values.rejectionReason && { rejectionReason: values.rejectionReason })
          });
          
          await dispatch(
            bulkChangeAdvertiseStatus({
              advertiseIds: values.advertiseIds,
              status: values.status,
              ...(values.rejectionReason && { rejectionReason: values.rejectionReason }),
            })
          ).unwrap();
          
          dispatch(setStatus({ status: 'idle' }));
          onClose();
          resetForm();
          setAdvertise(undefined);
        } else {
          // Handle single advertisement
          console.log("Sending single ad request with:", {
            advertiseId: values.advertiseId,
            status: values.status
          });
          
          await dispatch(
            createChangeAdvertiseStatus({
              advertiseId: values.advertiseId,
              status: values.status,
              ...(values.rejectionReason && { rejectionReason: values.rejectionReason }),
            })
          ).unwrap();
          
          dispatch(setStatus({ status: 'idle' }));
          
          // Only fetch record if we're in detail view
          if (advertiseId) {
            const updatedRecord = await dispatch(
              getAdvertise({ id: advertiseId })
            ).unwrap();
            setAdvertise(updatedRecord);
          }
          
          onClose();
          resetForm();
        }
      } catch (error: any) {
        console.error("Error changing status:", error);
        
        // Extract error message from various possible formats
        const errorMessage = error?.message || error?.payload?.message || error?.payload || error?.error?.message || String(error);
        const errorCode = error?.code || error?.payload?.code || error?.error?.code;
        
        // Check if it's the document requirement error (by message or code 400 with specific message)
        const isDocumentError = 
          errorMessage === 'title_deed_and_id_required_for_individual_ads' || 
          errorMessage?.includes('Title Deed and ID documents must be uploaded') ||
          errorMessage?.includes('title_deed_and_id_required') ||
          (errorCode === 400 && errorMessage?.includes('title_deed'));
        
        if (isDocumentError) {
          setDocumentError('Title Deed and ID documents must be uploaded before approving or rejecting individual user ads. Please upload the required documents first.');
          toast.error('Title Deed and ID documents must be uploaded before approving or rejecting individual user ads.');
        } else {
          // Show other errors via toast
          toast.error(errorMessage || 'An error occurred while changing the status');
        }
      }
    },
  });

  // Direct handler for the save button
  const handleSaveClick = async () => {
    try {
      console.log("Save button clicked directly");
      
      // If extending expiry, validate endDate
      if (isExtendExpiry) {
        if (!values.endDate) {
          toast.error("Please select an expiry date");
          return;
        }
        
        // Call API to extend expiry
        await dispatch(
          createChangeAdvertiseStatus({
            advertiseId: values.advertiseId,
            endDate: values.endDate, // Send endDate as Date object
          })
        ).unwrap();

        dispatch(setStatus({ status: 'idle' }));
        
        if (advertiseId) {
          const updatedRecord = await dispatch(
            getAdvertise({ id: advertiseId })
          ).unwrap();
          setAdvertise(updatedRecord);
        }
        
        toast.success('Expiry date extended successfully');
        setTimeout(() => {
          onClose();
          resetForm();
        }, 300);
        return;
      }
      
      // Validate form values manually for status change
      if (!values.status) {
        console.error("Status is required");
        return;
      }
      
      if ((values.status === AdvertiseStatus.rejected || values.status === AdvertiseStatus.suspended) 
          && !values.rejectionReason) {
        console.error("Rejection reason is required for rejected or suspended status");
        return;
      }
      
      // For company admins, when publishing, use credit system
      if (isCompanyAdmin && values.status === AdvertiseStatus.published) {
        // Handle bulk operations with credits
        if (values.isBulkAction && values.advertiseIds && values.advertiseIds.length > 0) {
          console.log("Publishing bulk ads with credits:", {
            advertiseIds: values.advertiseIds,
          });
          
          const result = await publishAdsWithCredits({
            advertiseIds: values.advertiseIds,
          });
          
          toast.success(result.message || `${result.publishedCount} ads published successfully using credits.`);
          dispatch(setStatus({ status: 'idle' }));
          
          if (typeof setAdvertise === 'function') {
            setAdvertise(undefined);
          }
          
          setTimeout(() => {
            onClose();
            resetForm();
          }, 300);
          return;
        } 
        // Handle single advertisement with credits
        else if (values.advertiseId) {
          console.log("Publishing single ad with credits:", {
            advertiseId: values.advertiseId,
          });
          
          const result = await publishAdsWithCredits({
            advertiseIds: [values.advertiseId],
          });
          
          toast.success(result.message || 'Ad published successfully using credits.');
          dispatch(setStatus({ status: 'idle' }));
          
          if (advertiseId) {
            const updatedRecord = await dispatch(
              getAdvertise({ id: advertiseId })
            ).unwrap();
            setAdvertise(updatedRecord);
          } else {
            if (typeof setAdvertise === 'function') {
              setAdvertise(undefined);
            }
          }
          
          setTimeout(() => {
            onClose();
            resetForm();
          }, 300);
          return;
        }
      }

      // For non-publish status or non-company users, use regular status change
      // For bulk operations
      if (values.isBulkAction && values.advertiseIds && values.advertiseIds.length > 0) {
        console.log("Executing bulk request directly:", {
          advertiseIds: values.advertiseIds,
          status: values.status,
          rejectionReason: values.rejectionReason
        });
        
        try {
          // First attempt: Using Redux action
          const result = await dispatch(
            bulkChangeAdvertiseStatus({
              advertiseIds: values.advertiseIds,
              status: values.status,
              ...(values.rejectionReason && { rejectionReason: values.rejectionReason }),
            })
          ).unwrap();
          
          console.log("Bulk change success using Redux:", result);
          
          // Important: Update the UI first before closing the modal
          dispatch(setStatus({ status: 'idle' }));
          
          // Explicitly call the callback function to refresh data
          // This ensures the parent component refreshes the data
          if (typeof setAdvertise === 'function') {
            console.log("Calling parent refresh callback");
            setAdvertise(undefined);
          }
          
          // Close the modal and reset only after UI update is triggered
          setTimeout(() => {
            onClose();
            resetForm();
          }, 300);
        } catch (reduxError) {
          console.error("API call failed. Token or authentication issue.", reduxError);
          throw reduxError; // Let the main try/catch handle this
        }
      } 
      // For single advertisement
      else if (values.advertiseId) {
        const result = await dispatch(
          createChangeAdvertiseStatus({
            advertiseId: values.advertiseId,
            status: values.status,
            ...(values.rejectionReason && { rejectionReason: values.rejectionReason }),
          })
        ).unwrap();
        
        console.log("Single ad status change success:", result);
        
        // Important: Refresh the data before closing
        dispatch(setStatus({ status: 'idle' }));
        
        if (advertiseId) {
          const updatedRecord = await dispatch(
            getAdvertise({ id: advertiseId })
          ).unwrap();
          setAdvertise(updatedRecord);
        } else {
          // For bulk mode in single selection, call the callback
          if (typeof setAdvertise === 'function') {
            console.log("Calling parent refresh callback for single ad action");
            setAdvertise(undefined);
          }
        }
        
        // Close the modal after UI update is triggered
        setTimeout(() => {
          onClose();
          resetForm();
        }, 300);
      }
    } catch (error: any) {
      console.error("Error saving status:", error);
      
      // Extract error message from various possible formats
      const errorMessage = error?.message || error?.payload?.message || error?.payload || error?.error?.message || String(error);
      const errorCode = error?.code || error?.payload?.code || error?.error?.code;
      
      // Check if it's the document requirement error (by message or code 400 with specific message)
      const isDocumentError = 
        errorMessage === 'title_deed_and_id_required_for_individual_ads' || 
        errorMessage?.includes('Title Deed and ID documents must be uploaded') ||
        errorMessage?.includes('title_deed_and_id_required') ||
        (errorCode === 400 && errorMessage?.includes('title_deed'));
      
      if (isDocumentError) {
        setDocumentError('Title Deed and ID documents must be uploaded before approving individual user ads. Please upload the required documents first.');
        toast.error('Title Deed and ID documents must be uploaded before approving individual user ads.');
        // Don't close the modal, let the user see the error message
        return;
      }
      
      // Show other errors via toast
      toast.error(errorMessage || 'An error occurred while changing the status');
      
      // Still call the parent refresh callback to ensure list updates
      if (typeof setAdvertise === 'function') {
        console.log("Calling parent refresh callback after error");
        setTimeout(() => {
          setAdvertise(undefined);
        }, 100);
      }
      
      // Attempt to close the modal and reset the form even after an error
      setTimeout(() => {
        onClose();
        resetForm();
      }, 300);
    }
  };

  // Debug function to check advertise props
  console.log("Modal advertise props:", { 
    isBulkAction: hasBulkActionProps(advertise) ? advertise.isBulkAction : false,
    selectedIds: hasBulkActionProps(advertise) ? advertise.selectedIds : [],
    status: advertise?.status
  });

  useEffect(() => {
    // Set appropriate values based on advertise status
    if (advertise) {
      if (isExtendExpiry) {
        // For extend expiry, set the current endDate if available
        // Type guard: check if it's Advertise type (not BulkActionAdvertise)
        if (!hasBulkActionProps(advertise) && advertise.endDate) {
          setFieldValue('endDate', new Date(advertise.endDate));
        }
      } else {
        // If status is already set to published (from publish button), keep it
        // Otherwise, set status for form based on advertise status
        if (advertise.status === AdvertiseStatus.published && isCompanyAdmin) {
          // Company admin clicked publish button - status is already set to published
          setFieldValue('status', AdvertiseStatus.published);
        } else {
          // Set status for form based on current advertise status
          setFieldValue('status', advertise.status || '');
        }
      }
      
      // Set bulk action props if available
      if (hasBulkActionProps(advertise)) {
        console.log("Setting bulk action props:", {
          isBulkAction: advertise.isBulkAction,
          selectedIds: advertise.selectedIds
        });
        setFieldValue('isBulkAction', advertise.isBulkAction);
        setFieldValue('advertiseIds', advertise.selectedIds);
      }
    }
    
    // Clear document error when modal opens/closes
    if (!toggle) {
      setDocumentError(null);
    }
  }, [advertise, toggle, isCompanyAdmin, isExtendExpiry, setFieldValue]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={toggle}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => {
        resetForm();
        onClose();
      }}
      aria-describedby='alert-dialog-slide-description'
    >
      <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>
        {isExtendExpiry 
          ? 'Extend Expiry Date' 
          : (values.isBulkAction ? 'Bulk Change Advertise Status' : 'Change Advertise Status')}
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Extend Expiry Date Section */}
          {isExtendExpiry ? (
            <>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>Extend Expiry Date</AlertTitle>
                  <Typography variant="body2">
                    Select a new expiry date for this advertisement. The ad will remain published if the new date is in the future.
                  </Typography>
                </Alert>
              </Grid>
              
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="New Expiry Date"
                    value={values.endDate}
                    onChange={(newValue) => {
                      setFieldValue('endDate', newValue);
                    }}
                    minDate={new Date()} // Prevent selecting past dates
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={Boolean(touched.endDate && errors.endDate)}
                        helperText={touched.endDate && errors.endDate ? String(errors.endDate) : ''}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          ) : (
            <>
              {/* Document Error Alert */}
              {documentError && (
                <Grid item xs={12}>
                  <Alert 
                    severity="error" 
                    onClose={() => setDocumentError(null)}
                    sx={{ mb: 2 }}
                  >
                    <AlertTitle>Documents Required</AlertTitle>
                    {documentError}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Please upload the following documents before approving or rejecting:
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        <li>Title Deed</li>
                        <li>ID Document</li>
                      </ul>
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        You can upload these documents in the "Required Documents" section on the ad view page.
                      </Typography>
                    </Box>
                  </Alert>
                </Grid>
              )}

              {/* Credit System Info for Company Admins */}
              {isCompanyAdmin && values.status === AdvertiseStatus.published && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>Publishing with Credits</AlertTitle>
                    <Typography variant="body2">
                      {values.isBulkAction && values.advertiseIds
                        ? `Publishing ${values.advertiseIds.length} ad(s) will use ${values.advertiseIds.length} credit(s) from your company wallet.`
                        : 'Publishing this ad will use 1 credit from your company wallet.'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Make sure you have sufficient credits available before publishing.
                    </Typography>
                  </Alert>
                </Grid>
              )}
              
              {/* Status Selection */}
              <Grid item xs={12} sm={12}>
                <FormControl
                  fullWidth
                  error={Boolean(touched.status && errors.status)}
                >
                  <InputLabel id='status'>{'Select Status'}</InputLabel>
                  <Select
                    name='status'
                    labelId='status'
                    id='status'
                    value={values.status}
                    label={'Select Status'}
                    onChange={handleChange}
                    MenuProps={{
                      style: {
                        maxHeight: 400,
                      },
                    }}
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {/* For bulk operations, show only allowed transitions */}
                    {values.isBulkAction && hasBulkActionProps(advertise) && advertise.status === AdvertiseStatus.submit ? (
                      // For submitted advertisements, allow only approve/reject
                      ADVERTISE_STATUS.map((status) => (
                        <MenuItem
                          key={status.name}
                          value={status.value}
                          sx={{
                            textTransform: 'capitalize',
                          }}
                        >
                          {status.value}
                        </MenuItem>
                      ))
                    ) : values.isBulkAction && hasBulkActionProps(advertise) && advertise.status === AdvertiseStatus.published ? (
                      // For published advertisements, allow only suspend
                      <MenuItem
                        value={AdvertiseStatus.suspended}
                        sx={{
                          textTransform: 'capitalize',
                        }}
                      >
                        {AdvertiseStatus.suspended}
                      </MenuItem>
                    ) : (
                      // Otherwise show all options
                      ADVERTISE_STATUS?.map((status) => (
                        <MenuItem
                          key={status.name}
                          value={status.value}
                          sx={{
                            textTransform: 'capitalize',
                          }}
                        >
                          {status.value}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {Boolean(touched.status && errors.status) && (
                    <FormHelperText>{errors.status}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Rejection Reason (only when status is rejected or suspended) */}
              {(values?.status === AdvertiseStatus.rejected || values?.status === AdvertiseStatus.suspended) && (
                <Grid item xs={12} sm={12}>
                  <TextField
                    multiline
                    rows={3}
                    fullWidth
                    label={'Rejection Reason'}
                    name='rejectionReason'
                    id='rejectionReason'
                    onChange={handleChange}
                    value={values.rejectionReason}
                    error={Boolean(
                      errors.rejectionReason && touched.rejectionReason
                    )}
                    helperText={
                      Boolean(
                        errors.rejectionReason && touched.rejectionReason
                      ) && errors?.rejectionReason
                    }
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            resetForm();
            onClose();
          }}
          variant='outlined'
        >
          {'Cancel'}
        </Button>
        <LoadingButton
          onClick={handleSaveClick}
          loading={isSubmitting}
          variant='contained'
          disabled={
            isExtendExpiry 
              ? !values.endDate 
              : !values.status || ((values.status === AdvertiseStatus.rejected || values.status === AdvertiseStatus.suspended) && !values.rejectionReason)
          }
        >
          {'Save'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeAdvertiseStatusFormModal;
